import { useEffect, useMemo, useState } from "react";
import {
  FiSearch,
  FiFilter,
  FiDownload,
  FiEye,
  FiTrendingUp,
  FiDollarSign,
  FiFileText,
  FiUsers,
  FiPackage,
} from "react-icons/fi";
import { reportApi } from "../services/reportApi";

const tabs = [
  { key: "all", label: "All" },
  { key: "finance", label: "Finance" },
  { key: "invoices", label: "Invoices" },
  { key: "vendors", label: "Vendors" },
  { key: "logistics", label: "Logistics" },
  { key: "payments", label: "Payments" },
  { key: "cost-prices", label: "Cost Prices" },
];

const emptyDashboard = {
  summary: {
    totalRevenue: 0,
    totalExpenses: 0,
    outstanding: 0,
    activeReports: 0,
    paidAmount: 0,
    overdueCount: 0,
    totalContainers: 0,
    completedPayments: 0,
    failedPayments: 0,
    averageVendorRating: 0,
  },
  trend: [],
  reports: [],
  insights: [],
  meta: {
    periodLabel: "Current snapshot",
    totalReports: 0,
    filteredReports: 0,
  },
};

const formatMoney = (value) =>
  `£${Number(value || 0).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const reportBadgeClass = (status) => {
  const s = String(status || "").toLowerCase();
  const base =
    "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold";
  if (s === "ready")
    return `${base} bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300`;
  if (s === "review")
    return `${base} bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300`;
  if (s === "pending")
    return `${base} bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300`;
  return `${base} bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300`;
};

const toneClass = (tone) => {
  if (tone === "positive") return "text-emerald-600 dark:text-emerald-400";
  if (tone === "warning") return "text-amber-600 dark:text-amber-400";
  if (tone === "negative") return "text-red-600 dark:text-red-400";
  return "text-blue-600 dark:text-blue-400";
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export default function Reports() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(emptyDashboard);

  const [search, setSearch] = useState("");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const [selectedReport, setSelectedReport] = useState(null);
  const [message, setMessage] = useState("");

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (search.trim()) params.set("search", search.trim());
    if (moduleFilter !== "all") params.set("module", moduleFilter);
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (fromDate) params.set("from", fromDate);
    if (toDate) params.set("to", toDate);
    const q = params.toString();
    return q ? `?${q}` : "";
  }, [search, moduleFilter, statusFilter, fromDate, toDate]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await reportApi.dashboard(queryString);
      const data = res.data || emptyDashboard;
      setDashboard({
        summary: { ...emptyDashboard.summary, ...(data.summary || {}) },
        trend: Array.isArray(data.trend) ? data.trend : [],
        reports: Array.isArray(data.reports) ? data.reports : [],
        insights: Array.isArray(data.insights) ? data.insights : [],
        meta: { ...emptyDashboard.meta, ...(data.meta || {}) },
      });

      setSelectedReport((prev) => {
        if (!data.reports || !data.reports.length) return null;
        const keep = data.reports.find((r) => r.id === prev?.id);
        return keep || data.reports[0];
      });
    } catch (err) {
      setMessage(err.response?.data?.message || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => loadDashboard(), 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  const maxTrend = Math.max(...dashboard.trend.map((t) => t.revenue || 0), 1);

  const handleExport = async () => {
    try {
      const res = await reportApi.exportCsv(queryString);
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      downloadBlob(blob, `reports-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (err) {
      setMessage(err.response?.data?.message || "Failed to export reports");
    }
  };

  const clearFilters = () => {
    setSearch("");
    setModuleFilter("all");
    setStatusFilter("all");
    setFromDate("");
    setToDate("");
  };

  const selected = selectedReport || dashboard.reports[0] || null;

  return (
    <div className="p-6 text-slate-900 transition-colors duration-300 dark:text-slate-100">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-100">
            Reports
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            View analytics, summaries, and business insights.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search reports..."
              className="w-72 rounded-xl border border-slate-200 bg-white px-4 py-3 pl-10 text-sm text-slate-900 outline-none transition focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
            />
          </div>

          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className={`flex items-center gap-2 rounded-xl border px-4 py-3 text-sm transition ${
              showFilters
                ? "border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
            }`}
          >
            <FiFilter />
            Filter
          </button>

          <button
            type="button"
            onClick={handleExport}
            className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-medium text-white transition hover:bg-blue-700"
          >
            <FiDownload />
            Export
          </button>
        </div>
      </div>

      {message ? (
        <div className="mb-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
          {message}
        </div>
      ) : null}

      {showFilters ? (
        <div className="mb-6 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Module
              </label>
              <select
                value={moduleFilter}
                onChange={(e) => setModuleFilter(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              >
                {tabs.map((tab) => (
                  <option key={tab.key} value={tab.key}>
                    {tab.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              >
                <option value="all">All</option>
                <option value="ready">Ready</option>
                <option value="review">Review</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                From
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                To
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              />
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={clearFilters}
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Clear filters
            </button>
          </div>
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Total Revenue"
          value={formatMoney(dashboard.summary.totalRevenue)}
          icon={<FiTrendingUp className="text-xl text-white" />}
          iconBg="bg-blue-600"
          delta={dashboard.insights?.[0]?.value || "+0%"}
          deltaClass="text-emerald-600 dark:text-emerald-400"
        />
        <StatCard
          title="Total Expenses"
          value={formatMoney(dashboard.summary.totalExpenses)}
          icon={<FiDollarSign className="text-xl text-white" />}
          iconBg="bg-emerald-500"
          delta={dashboard.summary.failedPayments ? `${dashboard.summary.failedPayments} failed` : "-"}
          deltaClass="text-rose-600 dark:text-rose-400"
        />
        <StatCard
          title="Outstanding"
          value={formatMoney(dashboard.summary.outstanding)}
          icon={<FiFileText className="text-xl text-white" />}
          iconBg="bg-amber-500"
          delta={`${dashboard.summary.overdueCount || 0} overdue`}
          deltaClass="text-emerald-600 dark:text-emerald-400"
        />
        <StatCard
          title="Active Reports"
          value={dashboard.summary.activeReports}
          icon={<FiPackage className="text-xl text-white" />}
          iconBg="bg-violet-500"
          delta={`${dashboard.meta.totalReports || 0} total`}
          deltaClass="text-emerald-600 dark:text-emerald-400"
        />
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-col gap-4 border-b border-slate-100 px-5 py-5 dark:border-slate-800">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Reports Overview
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Track business performance across all modules.
                </p>
              </div>

              <div className="text-sm text-slate-500 dark:text-slate-400">
                Showing {dashboard.reports.length} reports
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setModuleFilter(tab.key)}
                  className={`rounded-xl px-3 py-2 text-sm transition ${
                    moduleFilter === tab.key
                      ? "bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
                      : "text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="px-5 pb-5 pt-4">
            <div
              id="reports-table"
              className="overflow-hidden rounded-2xl border border-slate-100 dark:border-slate-800"
            >
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                    <tr>
                      <th className="px-4 py-3">Report Name</th>
                      <th className="px-4 py-3">Category</th>
                      <th className="px-4 py-3">Period</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Updated</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {loading ? (
                      <tr>
                        <td
                          colSpan="6"
                          className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400"
                        >
                          Loading reports...
                        </td>
                      </tr>
                    ) : dashboard.reports.length === 0 ? (
                      <tr>
                        <td
                          colSpan="6"
                          className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400"
                        >
                          No reports found.
                        </td>
                      </tr>
                    ) : (
                      dashboard.reports.map((row) => (
                        <tr
                          key={row.id}
                          className="bg-white transition hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800/60"
                        >
                          <td className="px-4 py-4">
                            <div className="font-semibold text-slate-900 dark:text-slate-100">
                              {row.reportName}
                            </div>
                            <div className="text-sm text-slate-500 dark:text-slate-400">
                              {row.summary}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">
                            {row.category}
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">
                            {row.period}
                          </td>
                          <td className="px-4 py-4">
                            <span className={reportBadgeClass(row.status)}>
                              {row.status}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">
                            {row.updated}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => setSelectedReport(row)}
                                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                              >
                                View
                              </button>
                              <button
                                type="button"
                                onClick={handleExport}
                                className="rounded-xl border border-blue-200 px-3 py-2 text-sm text-blue-600 transition hover:bg-blue-50 dark:border-blue-900/50 dark:text-blue-300 dark:hover:bg-blue-950/40"
                              >
                                Export
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between text-sm text-slate-500 dark:text-slate-400">
              <span>Total Reports: {dashboard.meta.totalReports}</span>
              <span>{dashboard.meta.periodLabel}</span>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="border-b border-slate-100 px-5 py-5 dark:border-slate-800">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Revenue Trend
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Monthly performance chart
              </p>
            </div>

            <div className="px-5 py-5">
              <div className="flex h-72 items-end gap-3">
                {dashboard.trend.map((item) => (
                  <div key={item.label} className="flex flex-1 flex-col items-center gap-2">
                    <div className="flex h-56 w-full items-end justify-center">
                      <div
                        className="w-full max-w-[44px] rounded-t-2xl bg-blue-600"
                        style={{ height: `${Math.max(8, (item.revenue / maxTrend) * 100)}%` }}
                        title={formatMoney(item.revenue)}
                      />
                    </div>
                    <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                      {item.percentage || 0}%
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {item.label.split(" ")[0]}
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={() =>
                  document.getElementById("reports-table")?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  })
                }
                className="mt-4 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                View Full Analytics
              </button>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="border-b border-slate-100 px-5 py-5 dark:border-slate-800">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Quick Insights
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Key business signals
              </p>
            </div>

            <div className="space-y-3 px-5 py-5">
              {dashboard.insights.map((item) => (
                <div
                  key={item.label}
                  className="rounded-2xl bg-slate-50 p-4 transition-colors duration-300 dark:bg-slate-950"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-medium text-slate-800 dark:text-slate-100">
                        {item.label}
                      </div>
                      <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        {item.detail}
                      </div>
                    </div>
                    <div className={`font-semibold ${toneClass(item.tone)}`}>
                      {item.value}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="border-b border-slate-100 px-5 py-5 dark:border-slate-800">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Selected Report
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Report details and key metrics
              </p>
            </div>

            <div className="px-5 py-5">
              {selected ? (
                <div>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-base font-semibold text-slate-900 dark:text-slate-100">
                        {selected.reportName}
                      </div>
                      <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        {selected.category} • {selected.period}
                      </div>
                    </div>
                    <span className={reportBadgeClass(selected.status)}>
                      {selected.status}
                    </span>
                  </div>

                  <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">
                    {selected.summary}
                  </p>

                  <div className="mt-5 grid grid-cols-2 gap-3">
                    {(selected.details || []).map((item) => (
                      <div
                        key={item.label}
                        className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950"
                      >
                        <div className="text-xs uppercase text-slate-500 dark:text-slate-400">
                          {item.label}
                        </div>
                        <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                          {item.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  Select a report to see details.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon, iconBg, delta, deltaClass }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-colors duration-300 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">{title}</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-100">
            {value}
          </p>
          <p className={`mt-2 text-sm font-medium ${deltaClass}`}>{delta}</p>
        </div>
        <div className={`grid h-12 w-12 place-items-center rounded-2xl ${iconBg}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}